import { DOCUMENT } from '@angular/common';
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewEncapsulation
} from '@angular/core';

declare const P: any;

@Component({
  selector: 'app-placetopay-payment',
  templateUrl: './placetopay.component.html',
  styleUrls: ['./placetopay.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class PlacetopayComponent implements OnInit, OnDestroy {
  private static readonly SCRIPT_ID = 'placetopay-script';
  private static readonly CLOUD_FRONT_TEST_ID = 'placetopay-cloudfront-test';
  private static readonly WARNING_ID = 'placetopay-js-warning';
  private static readonly WARNING_OVERLAY_ID = 'placetopay-js-warning-overlay';
  private static readonly FALLBACK_CLASS = 'placetopay-fallback-active';
  private static readonly LIGHTBOX_CSS_HREF_FRAGMENT = 'lightbox.css';
  private static readonly FALSE_ORIGIN_SNIPPET = 'placetopay';
  private static readonly MAIN_SCRIPT_URL = 'https://checkout.placetopay.ec/lightbox.min.js';
  private static readonly CLOUD_FRONT_URL = 'https://d3u0e7dp0ijq42.cloudfront.net/assets/vendor-izSu0QU9.js';
  private static readonly LIGHTBOX_SELECTOR = '.redirection-lightbox';
  private static readonly LIGHTBOX_IFRAME_SELECTOR = '.redirection-lightbox iframe';
  private static readonly WARNING_DELAY_MS = 5000;

  @Input() secundaryColor = '#000';
  @Input() loading = false;
  @Input() totalToPay = 0;
  @Input() block = false;
  @Input() termsCondictios = false;
  @Input() buttonType: 'checkout' | 'lightbox' = 'checkout';
  @Output() executeCheckout = new EventEmitter<void>();
  @Output() executeLightbox = new EventEmitter<void>();
  @Output() onLightboxClose = new EventEmitter<void>();
  @Output() onLightboxResponse = new EventEmitter<any>();
  @Output() onPlacetopayBlocked = new EventEmitter<string>();

  paymentHandler: any = null;
  private isPlacetopayReady = false;
  private static eventsRegistered = false;
  private static activeInstance: PlacetopayComponent | null = null;
  private static isProcessingClose = false;
  private static cspViolationDetected = false;
  private static cspListenerRegistered = false;

  private warningListeners: Array<() => void> = [];

  private readonly warningContent = {
    title: '⚠️ JavaScript Required',
    message: 'The payment page cannot load because JavaScript is disabled or blocked in your browser.',
    hint: 'Please enable JavaScript and disable any script-blocking extensions (like NoScript, uBlock Origin, or similar) to proceed with the payment.',
    buttonLabel: 'Close'
  };

  constructor(
    private readonly ngZone: NgZone,
    private readonly renderer: Renderer2,
    @Inject(DOCUMENT) private readonly document: Document
  ) {}

  ngOnInit(): void {
    this.registerCspListener();
    this.invokePlacetopay();
  }

  ngOnDestroy(): void {
    // Don't remove warning elements if they're being displayed
    // The warning should persist even if the component is destroyed
    const warningExists = !!this.document.getElementById(PlacetopayComponent.WARNING_ID);

    if (!warningExists) {
      this.detachWarningListeners();
      this.removeWarningElements();
    } else {
      console.log('PlaceToPay: Warning dialog active, preserving it during component destruction');
    }

    this.renderer.removeClass(this.document.body, PlacetopayComponent.FALLBACK_CLASS);
  }

  makePaymentCheckout(): void {
    if (!this.isPlacetopayScriptAvailable()) {
      const errorMsg =
        '⚠️ JavaScript is disabled or PlaceToPay script is blocked. Please enable JavaScript and disable any script-blocking extensions to proceed with the payment.';
      alert(errorMsg);
      console.error('PlaceToPay: Script not available or not functional');
      this.onPlacetopayBlocked.emit(errorMsg);
      return;
    }

    if (PlacetopayComponent.cspViolationDetected) {
      const errorMsg =
        '⚠️ Payment service is blocked by security policy. Please disable any script-blocking extensions or browser security settings that may be blocking the payment gateway.';
      alert(errorMsg);
      console.error('PlaceToPay: Blocked by Content Security Policy');
      this.onPlacetopayBlocked.emit(errorMsg);
      return;
    }

    this.executeCheckout.emit();
  }

  makePaymentLightbox(): void {
    if (!this.isPlacetopayScriptAvailable()) {
      const errorMsg =
        '⚠️ JavaScript is disabled or PlaceToPay script is blocked. Please enable JavaScript and disable any script-blocking extensions to proceed with the payment.';
      alert(errorMsg);
      console.error('PlaceToPay: Script not available or not functional');
      this.onPlacetopayBlocked.emit(errorMsg);
      return;
    }

    if (PlacetopayComponent.cspViolationDetected) {
      const errorMsg =
        '⚠️ Payment service is blocked by security policy. Please disable any script-blocking extensions or browser security settings that may be blocking the payment gateway.';
      alert(errorMsg);
      console.error('PlaceToPay: Blocked by Content Security Policy');
      this.onPlacetopayBlocked.emit(errorMsg);
      return;
    }

    this.executeLightbox.emit();
  }

  openLightbox(processUrl: string): void {
    if (!this.isPlacetopayScriptAvailable()) {
      const errorMsg = 'PlaceToPay script is not available. Cannot open lightbox.';
      console.error('PlaceToPay:', errorMsg);
      this.onPlacetopayBlocked.emit(errorMsg);
      return;
    }

    if (PlacetopayComponent.cspViolationDetected) {
      const errorMsg = 'PlaceToPay is blocked by Content Security Policy. Cannot open lightbox.';
      console.error('PlaceToPay:', errorMsg);
      this.onPlacetopayBlocked.emit(errorMsg);
      return;
    }

    if (!processUrl || !processUrl.trim()) {
      const errorMsg = 'PlaceToPay: Invalid or empty processUrl';
      console.error(errorMsg);
      this.onPlacetopayBlocked.emit(errorMsg);
      return;
    }

    console.log('PlaceToPay: Verifying iframe connectivity before opening lightbox...');

    this.verifyIframeConnectivity().then((canConnect) => {
      if (canConnect) {
        console.log('PlaceToPay: Iframe connectivity verified, opening lightbox');
        this.openLightboxInternal(processUrl);
      } else {
        console.error('PlaceToPay: Iframe connectivity failed - JavaScript may be blocked');
        this.showJavaScriptBlockedWarning();
      }
    });
  }

  private registerCspListener(): void {
    if (PlacetopayComponent.cspListenerRegistered) {
      return;
    }

    PlacetopayComponent.cspListenerRegistered = true;

    window.addEventListener('securitypolicyviolation', (event: SecurityPolicyViolationEvent) => {
      const blockedUrl = event.blockedURI || '';
      const violatedDirective = event.violatedDirective || '';
      const isPlacetopayRelated =
        blockedUrl.includes('placetopay') ||
        blockedUrl.includes('cloudfront.net') ||
        blockedUrl.includes('d3u0e7dp0ijq42') ||
        blockedUrl.includes('checkout.placetopay');

      const isScriptBlocking = violatedDirective.includes('script-src');

      if (isPlacetopayRelated && isScriptBlocking) {
        console.error('PlaceToPay CSP Script Violation detected (blocking):', {
          blockedURI: event.blockedURI,
          violatedDirective: event.violatedDirective,
          originalPolicy: event.originalPolicy
        });
        PlacetopayComponent.cspViolationDetected = true;

        if (PlacetopayComponent.activeInstance) {
          const activeInstance = PlacetopayComponent.activeInstance;
          activeInstance.ngZone.run(() => {
            activeInstance.onPlacetopayBlocked.emit('PlaceToPay script blocked by CSP: ' + blockedUrl);
          });
        }
      } else if (isPlacetopayRelated) {
        console.warn('PlaceToPay CSP Violation (non-blocking, style or other):', {
          blockedURI: event.blockedURI,
          violatedDirective: event.violatedDirective
        });
      }
    });
  }

  private invokePlacetopay(): void {
    const existingScript = this.document.getElementById(PlacetopayComponent.SCRIPT_ID) as HTMLScriptElement | null;

    if (!existingScript) {
      const script = this.renderer.createElement('script');
      this.renderer.setAttribute(script, 'id', PlacetopayComponent.SCRIPT_ID);
      this.renderer.setAttribute(script, 'type', 'text/javascript');
      this.renderer.setAttribute(script, 'src', PlacetopayComponent.MAIN_SCRIPT_URL);

      script.onload = () => {
        this.ngZone.run(() => {
          this.isPlacetopayReady = true;
          this.setupPlacetopayEvents();
          this.testCloudFrontDependency();
        });
      };

      script.onerror = () => {
        this.ngZone.run(() => {
          console.error('PlaceToPay: Failed to load main script');
          PlacetopayComponent.cspViolationDetected = true;
          this.onPlacetopayBlocked.emit('PlaceToPay main script failed to load');
        });
      };

      this.renderer.appendChild(this.document.body, script);
      return;
    }

    this.isPlacetopayReady = true;
    this.setupPlacetopayEvents();
  }

  private testCloudFrontDependency(): void {
    const testScript = this.renderer.createElement('script');
    this.renderer.setAttribute(testScript, 'id', PlacetopayComponent.CLOUD_FRONT_TEST_ID);
    this.renderer.setAttribute(testScript, 'type', 'text/javascript');
    this.renderer.setAttribute(testScript, 'src', PlacetopayComponent.CLOUD_FRONT_URL);

    testScript.onerror = () => {
      this.ngZone.run(() => {
        console.error('PlaceToPay: CloudFront dependency blocked by CSP');
        PlacetopayComponent.cspViolationDetected = true;
        this.onPlacetopayBlocked.emit('PlaceToPay CloudFront dependencies are blocked');
      });
      this.removeNode(testScript);
    };

    testScript.onload = () => {
      console.log('PlaceToPay: CloudFront dependencies verified');
      this.removeNode(testScript);
    };

    this.renderer.appendChild(this.document.body, testScript);
  }

  private setupPlacetopayEvents(): void {
    if (PlacetopayComponent.eventsRegistered || typeof P === 'undefined') {
      return;
    }

    PlacetopayComponent.eventsRegistered = true;

    P.on('close', () => {
      if (PlacetopayComponent.isProcessingClose) {
        return;
      }
      PlacetopayComponent.isProcessingClose = true;

      const activeInstance = PlacetopayComponent.activeInstance;
      if (activeInstance) {
        activeInstance.ngZone.run(() => {
          activeInstance.onLightboxClose.emit();
        });
      }

      setTimeout(() => {
        PlacetopayComponent.isProcessingClose = false;
      }, 500);
    });

    P.on('response', (response: any) => {
      const activeInstance = PlacetopayComponent.activeInstance;
      if (activeInstance) {
        activeInstance.ngZone.run(() => {
          activeInstance.onLightboxResponse.emit(response);
        });
      }
    });
  }

  private isPlacetopayScriptAvailable(): boolean {
    try {
      if (typeof P === 'undefined') {
        return false;
      }
      if (typeof P.init !== 'function') {
        return false;
      }
      if (typeof P.on !== 'function') {
        return false;
      }
      return true;
    } catch (error) {
      console.error('PlaceToPay: Error checking script availability', error);
      return false;
    }
  }

  private verifyIframeConnectivity(): Promise<boolean> {
    return new Promise((resolve) => {
      if (PlacetopayComponent.cspViolationDetected) {
        console.log('PlaceToPay: CSP script violation previously detected');
        resolve(false);
        return;
      }

      if (typeof P === 'undefined' || typeof P.init !== 'function') {
        console.log('PlaceToPay: Script not available');
        resolve(false);
        return;
      }

      console.log('PlaceToPay: Basic pre-check passed');
      resolve(true);
    });
  }

  private openLightboxInternal(processUrl: string): void {
    if (typeof P === 'undefined' || !this.isPlacetopayReady) {
      const errorMsg = 'PlaceToPay no está listo. Asegúrese de que el script se haya cargado correctamente.';
      console.error(errorMsg);
      this.onPlacetopayBlocked.emit(errorMsg);
      return;
    }

    PlacetopayComponent.activeInstance = this;
    PlacetopayComponent.isProcessingClose = false;

    console.log('PlaceToPay: Opening lightbox with URL:', processUrl);

    try {
      this.ensureLightboxStyles();
      P.init(processUrl);
      console.log('PlaceToPay: P.init() called successfully');
      this.monitorRealLightbox();
    } catch (error) {
      console.error('PlaceToPay: Error initializing lightbox', error);
      PlacetopayComponent.cspViolationDetected = true;
      this.onPlacetopayBlocked.emit('Error initializing PlaceToPay lightbox');
    }
  }

  private monitorRealLightbox(): void {
    let messageReceived = false;

    const messageHandler = (event: MessageEvent) => {
      const origin = event.origin || '';
      if (origin.includes(PlacetopayComponent.FALSE_ORIGIN_SNIPPET)) {
        console.log('PlaceToPay: Lightbox is alive - received postMessage');
        messageReceived = true;
        window.removeEventListener('message', messageHandler);
      }
    };

    window.addEventListener('message', messageHandler);

    setTimeout(() => {
      const lightboxOverlay = this.document.querySelector(PlacetopayComponent.LIGHTBOX_SELECTOR);

      if (!lightboxOverlay) {
        console.log('PlaceToPay: Lightbox was closed by user');
        window.removeEventListener('message', messageHandler);
        return;
      }

      if (messageReceived) {
        console.log('PlaceToPay: Lightbox confirmed working');
        return;
      }

      const iframe = this.document.querySelector(
        PlacetopayComponent.LIGHTBOX_IFRAME_SELECTOR
      ) as HTMLIFrameElement | null;

      if (iframe && iframe.src && iframe.src.includes(PlacetopayComponent.FALSE_ORIGIN_SNIPPET)) {
        console.log('PlaceToPay: Lightbox iframe has valid src');

        console.error('PlaceToPay: No response from lightbox - JavaScript may be blocked');
        window.removeEventListener('message', messageHandler);

        this.ngZone.run(() => {
          this.closeLightboxFrame();
          this.showJavaScriptBlockedWarning();
        });
      } else {
        window.removeEventListener('message', messageHandler);
      }
    }, PlacetopayComponent.WARNING_DELAY_MS);
  }

  private showJavaScriptBlockedWarning(): void {
    console.error('PlaceToPay: JavaScript appears to be blocked in the payment iframe');

    if (this.document.getElementById(PlacetopayComponent.WARNING_ID)) {
      console.warn('PlaceToPay: Warning dialog already displayed');
      return;
    }

    // Create and show warning using raw DOM manipulation for maximum compatibility
    this.closeLightboxFrame();

    const overlay = this.document.createElement('div');
    overlay.id = PlacetopayComponent.WARNING_OVERLAY_ID;
    overlay.className = 'placetopay-warning-overlay';
    overlay.setAttribute('style',
      'position:fixed !important;top:0 !important;left:0 !important;width:100% !important;' +
      'height:100% !important;background:rgba(0, 0, 0, 0.6) !important;display:block !important;' +
      'z-index:99999999 !important;pointer-events:auto !important;margin:0 !important;padding:0 !important;'
    );

    const wrapper = this.document.createElement('div');
    wrapper.id = PlacetopayComponent.WARNING_ID;
    wrapper.className = 'placetopay-warning';
    wrapper.setAttribute('style',
      'position:fixed !important;top:50% !important;left:50% !important;' +
      'transform:translate(-50%, -50%) !important;width:90% !important;max-width:420px !important;' +
      'background:#ffffff !important;padding:32px !important;border-radius:12px !important;' +
      'box-shadow:0 4px 24px rgba(0, 0, 0, 0.3) !important;text-align:center !important;' +
      'display:block !important;z-index:100000000 !important;pointer-events:auto !important;' +
      'margin:0 !important;'
    );

    const title = this.document.createElement('h3');
    title.className = 'placetopay-warning__title';
    title.textContent = this.warningContent.title;

    const message = this.document.createElement('p');
    message.className = 'placetopay-warning__message';
    message.textContent = this.warningContent.message;

    const hint = this.document.createElement('p');
    hint.className = 'placetopay-warning__hint';
    hint.textContent = this.warningContent.hint;

    const button = this.document.createElement('button');
    button.className = 'placetopay-warning__button';
    button.textContent = this.warningContent.buttonLabel;

    wrapper.appendChild(title);
    wrapper.appendChild(message);
    wrapper.appendChild(hint);
    wrapper.appendChild(button);

    this.document.body.appendChild(overlay);
    this.document.body.appendChild(wrapper);

    console.log('PlaceToPay: Warning dialog inserted into DOM');
    console.log('PlaceToPay: Document body children count:', this.document.body.children.length);
    console.log('PlaceToPay: Overlay element:', this.document.getElementById(PlacetopayComponent.WARNING_OVERLAY_ID));
    console.log('PlaceToPay: Warning element:', this.document.getElementById(PlacetopayComponent.WARNING_ID));
    console.log('PlaceToPay: Body overflow:', this.document.body.style.overflow);
    console.log('PlaceToPay: Body visibility:', this.document.body.style.visibility);
    console.log('PlaceToPay: Body display:', this.document.body.style.display);

    // Prevent clicks on the dialog from closing it
    wrapper.addEventListener('click', (event: Event) => {
      event.stopPropagation();
      console.log('PlaceToPay: Click on dialog (prevented from closing)');
    });

    // Add event listeners after a small delay to prevent immediate dismissal
    setTimeout(() => {
      this.ngZone.run(() => {
        this.detachWarningListeners();

        console.log('PlaceToPay: Attaching event listeners to warning dialog');

        this.warningListeners = [
          this.renderer.listen(button, 'click', (event: Event) => {
            console.log('PlaceToPay: Button clicked - dismissing warning');
            event.stopPropagation();
            this.ngZone.run(() => this.dismissWarning());
          }),
          this.renderer.listen(overlay, 'click', (event: Event) => {
            console.log('PlaceToPay: Overlay clicked - dismissing warning');
            this.ngZone.run(() => this.dismissWarning());
          })
        ];

        this.onPlacetopayBlocked.emit('JavaScript is blocked in payment iframe');
      });
    }, 300);
  }

  private ensureLightboxStyles(): void {
    const lightboxStylesheet = this.document.querySelector(
      `link[href*="${PlacetopayComponent.LIGHTBOX_CSS_HREF_FRAGMENT}"]`
    );

    if (lightboxStylesheet) {
      this.renderer.removeClass(this.document.body, PlacetopayComponent.FALLBACK_CLASS);
      console.log('PlaceToPay: lightbox.css is loaded');
      return;
    }

    if (!this.document.body.classList.contains(PlacetopayComponent.FALLBACK_CLASS)) {
      console.warn('PlaceToPay: lightbox.css not found, applying fallback styles');
      this.renderer.addClass(this.document.body, PlacetopayComponent.FALLBACK_CLASS);
    }
  }

  private closeLightboxFrame(): void {
    if (typeof P !== 'undefined' && typeof P.closeFrame === 'function') {
      try {
        P.closeFrame();
      } catch (error) {
        console.log('PlaceToPay: No lightbox to close');
      }
    }
  }

  private dismissWarning(): void {
    console.log('PlaceToPay: dismissWarning() called');
    this.removeWarningElements();
    this.detachWarningListeners();
  }

  private removeWarningElements(): void {
    console.log('PlaceToPay: Removing warning elements from DOM');
    const overlay = this.document.getElementById(PlacetopayComponent.WARNING_OVERLAY_ID);
    const warning = this.document.getElementById(PlacetopayComponent.WARNING_ID);

    if (overlay && overlay.parentNode) {
      this.renderer.removeChild(overlay.parentNode, overlay);
    }

    if (warning && warning.parentNode) {
      this.renderer.removeChild(warning.parentNode, warning);
    }
  }

  private detachWarningListeners(): void {
    this.warningListeners.forEach((unsubscribe) => unsubscribe());
    this.warningListeners = [];
  }

  private removeNode(node: HTMLElement): void {
    if (node && node.parentNode) {
      this.renderer.removeChild(node.parentNode, node);
    }
  }
}
